<script lang="ts" setup>
	import { useSeoSchema } from '~/utils/seo-schema'
	import type { H1Config } from '~/components/ui/h1.vue'
	import type { FAQContext } from '~/components/main/faq.vue'
	import type { usedHospitalContext } from '~/components/main/most-used-hospitals.vue'
	import type { teamBenefitsContext } from '~/components/main/productivity-benefits.vue'
	import type { GuideListContext } from '~/components/molecules/important-guides.vue'
	import type { ProcessesStepContext } from '~/components/molecules/process-steps.vue'

	import { useMediaQuery } from '@vueuse/core'
	import { HealthProviders } from '~/server/type'
	import { ProductCode } from '~/types'

	definePageMeta({
		layout: 'default'
	})
	const seo = {
		title: 'MyCoverGenius | Flexicare | Affordable HMO for businesses',
		description: 'Quality and affordable health insurance for your employees',
		tags: ['Health insurance'],
		url: `${import.meta.env.VITE_BUSINESS_URL}/flexicare`
	}
	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		articleTag: seo.tags,
		ogUrl: seo.url,
		twitterTitle: seo.title,
		twitterDescription: seo.description,
		twitterSite: '@mycovergenius',
		twitterCard: 'summary_large_image',
		twitterAppUrlGoogleplay: seo.url,
		twitterAppUrlIphone: seo.url,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})

	const isDialogOpen = shallowRef(false)
	const showBenefits = shallowRef(false)
	const focusMode = shallowRef(false)
	const { navigateToProduct, viewMoreProducts, getProductByGroupById } = useProductsStore()
	const isMobile = useMediaQuery('(max-width: 767px)')

	const flexiCareDetail = computed(() => getProductByGroupById('flexicare'))

	const faqList = ref<FAQContext[]>([
		{
			question: 'When will my policy become active after I activate it?',
			answer: 'Your H.M.O policy will become active 24 hours after activation. You can start accessing care once the activation period is complete.',
			open: true
		},
		{
			question: 'How do I access the list of hospitals on FlexiCare?',
			answer: "You can see the hospital list directly on the FlexiCare page or access it from your dashboard once you're logged in. This will provide you with a comprehensive list of all the hospitals where you can receive care under your plan.",
			open: false
		},
		{
			question: 'What is the least number of staff to add on FlexiCare?',
			answer: 'As a business, the minimum number of staff you can add on the FlexiCare plan is 2.',
			open: false
		},
		{
			question: 'Can I remove a staff from my plan when he/she leaves the company?',
			answer: 'Yes, you can easily remove staff members who no longer work at your company, either manually or in batches. Your dashboard enables this to help simplify plan and employee management.',
			open: false
		},
		{
			question: 'Is there a penalty for late renewal?',
			answer: 'If you renew within <span class="font-semibold">7 days after expiry</span>, your plan will reactivate within 24 hours. If you renew after <span class="font-semibold">7 days of expiry</span>, your plan will have a <span class="font-semibold">7-day waiting period</span> before it becomes active.',
			open: false
		}
	])

	const { generateProductSchema, generateFaqSchema, generateBreadcrumbSchema } = useSeoSchema()

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateProductSchema({
						name: 'Flexicare',
						description: seo.description,
						image: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082905/black-business-owner-lg_plehjx.webp',
						url: seo.url,
						price: '2000'
					})
				)
			},
			{ type: 'application/ld+json', innerHTML: JSON.stringify(generateFaqSchema(faqList.value)) },
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateBreadcrumbSchema([
						{ name: 'Home', item: 'https://mycovergenius.com/' },
						{ name: 'Flexicare', item: seo.url }
					])
				)
			}
		],
		link: [{ rel: 'canonical', href: seo.url }]
	})

	const onboardingProcesses = shallowRef<ProcessesStepContext>([
		{
			icon: {
				dir: 'random',
				icon: 'shopping-cart',
				height: '80',
				width: '78'
			},
			title: 'Choose your plan',
			description: 'Select any Flexicare plan that best fits your <br /> business needs'
		},
		{
			icon: {
				dir: 'random',
				icon: 'wishlist-blue',
				width: '60',
				height: '80'
			},
			title: 'Onboard your staff',
			description: 'Onboard your staff through our simple <br /> batch upload process'
		},
		{
			icon: {
				dir: 'random',
				icon: 'umbrella-2',
				height: '80',
				width: '80'
			},
			title: 'Access healthcare instantly',
			description:
				'Staff can now access care at their preferred <br/> hospital with their digital HMO ID. It’s that simple'
		}
	])

	const mostlyUsedHospitals: usedHospitalContext[] = [
		{
			name: {
				text: 'Faith Mediplex'
			},
			location: {
				text: '1 Giwa Amu/Airport Road, Benin'
			}
		},
		{
			name: {
				text: 'King trust hospital'
			},
			location: {
				text: '105 Ohunlana Drive, Surulere, Lagos'
			}
		},
		{
			name: {
				text: 'Lily hospital'
			},
			location: {
				text: '18, Edo-Osagie Street, GRA, Benin City, Edo State'
			}
		},
		{
			name: {
				text: 'Garki hospital'
			},
			location: {
				text: 'Tafawa Balewa Way, Area 3, Garki, Abuja'
			}
		},
		{
			name: {
				text: 'Lily hospital'
			},
			location: {
				text: '6, Brisibe St, Off Etuwewe Road, Warri, Delta State'
			}
		},
		{
			name: {
				text: 'Isalu hospital'
			},
			location: {
				text: '349B, Odusami Street, Off Wempco Road, By AY Hotels, Ogba Ikeja, Lagos'
			}
		},
		{
			name: {
				text: 'Westcare hospital'
			},
			location: {
				text: '31, Ifoshin Road, Pipeline Bus stop, Opposite Garislam Mosque, Orilowo Ejigbo, Lagos'
			}
		},
		{
			name: {
				text: 'Oak hospital'
			},
			location: {
				text: '191, Lagos Road, Ikorodu'
			}
		},
		{
			name: {
				text: 'Med-in specialist'
			},
			location: {
				text: '1, Osogbo Street, Off Celestial Way, Ogudu, Ojota'
			}
		},
		{
			name: {
				text: 'Vigor hospital'
			},
			location: {
				text: '10, Kingdom hall street, Off Fidiso Estate Abijo, Ibeju Lekki'
			}
		},
		{
			name: {
				text: 'Molly hospital '
			},
			location: {
				text: 'Idi-Ape, Iwo Road, Ibadan'
			}
		}
	]

	const btnClass: H1Config = {
		tag: { default: 'bg-main text-white sm:hidden mb-5' },
		wrapperClass: {
			alignItems: '',
			overflow: ''
		},
		hClass: {
			wrapper: {
				alignItems: 'items-start',
				text: 'text-pretty',
				base: 'sm:pr-2',
				size: 'text-[2.75rem]/[3.135rem] -tracking-[0.09rem] md:text-[3.5rem]/[4.2rem] md:-tracking-[0.1rem] lg:text-[4.25rem]/[5rem] lg:-tracking-[0.1275rem]'
			}
		},
		paragraphClass: {
			maxWidth: 'max-w-[634px] md:leading-[32px]',
			text: 'lg:text-xl text-base md:text-lg'
		},
		btn: {
			base: 'mt- md:mt-20',
			space: '',
			iconWrapper: {
				base: ' flex-col inline-flex'
			},
			primary: {
				btn: {
					base: 'bg-white  text-base text-health-800 hover:text-white',
					onHoverPseudo: 'hover:before:bg-health-500'
				},
				text: {
					size: 'text-sm md:text-base'
				}
			},
			secondary: {
				base: 'default-btn text-center justify-center gap-x-3',
				text: 'text-sm md:text-base'
			}
		}
	}
	const guideList: GuideListContext = [
		{
			tab: 'hospital',
			label: 'Hospital Visit',
			description: 'The best hospital experience for your staff',
			list: [
				{
					iconTag: {
						dir: 'random',
						icon: 'gps'
					},
					title: 'Locate the hospital of your choice',
					description:
						'Easily find the hospital that suits your needs by browsing our extensive network of healthcare facilities.',
					imageSrc: '/v1727082818/hospital-visit-gif_js3l8n.gif',
					show: true
				},
				{
					title: 'Present HMO ID',
					description: "Once you've chosen your hospital, simply present your HMO ID at the reception desk.",
					iconTag: {
						dir: 'random',
						icon: 'id-card'
					},
					imageSrc: '/v1727082838/present-hmo_h2trr2.gif'
				},
				{
					title: 'Get access to treatment instantly',
					description: "With your HMO ID ready, you'll immediately access treatment.",
					iconTag: {
						dir: 'random',
						icon: 'clipboard-text'
					},
					imageSrc: '/v1727082827/instant-treatment-access_q0l34b.gif'
				}
			]
		},
		{
			tab: 'virtual',
			label: 'Virtual Consultation',
			description: 'Your staff can access healthcare support professionals from their comfort',
			list: [
				{
					iconTag: {
						dir: 'random',
						icon: 'monitor-mobile'
					},
					title: 'Online consultation',
					description: 'Contact a healthcare professional from your comfort.',
					imageSrc: '/v1727082835/online-consultation_vbujcd.gif',
					show: true
				},
				{
					iconTag: {
						dir: 'fill',
						icon: 'special-consultation',
						pathClass: 'fill-white',
						height: 16,
						width: 16
					},
					title: 'Get answers fast',
					description: 'Receive fast health assessment and advice from a medical professional.',
					imageSrc: '/v1727082978/chat_bmnr01.webp'
				},
				{
					iconTag: {
						dir: 'random',
						icon: 'drug'
					},
					title: 'Pick up your prescription',
					description: 'Pick up your prescriptions from the nearest pharmacy within our network',
					imageSrc: '/v1727362076/instant-treatment-access_qz7ddk.webp'
				}
			]
		}
	]
	const teamBenefits: teamBenefitsContext = [
		{
			title: 'Minor & major surgeries',
			description: 'This plan covers your staff for surgical <br /> procedures.',
			icon: {
				icon: 'health-heart',
				dir: 'fill',
				height: 60,
				width: 60
			}
		},
		{
			title: 'Delivery and antenatal',
			description:
				'Get comprehensive coverage for delivery <br /> and antenatal care, including C-section procedures.',
			icon: {
				icon: 'pregnant',
				dir: 'fill',
				height: 80,
				width: 80
			}
		},
		{
			title: 'Hospital admission',
			description: 'Your staff enjoy quality healthcare while <br /> admitted at any of our hospitals.',
			icon: {
				icon: 'hospital-admission',
				dir: 'fill',
				width: 80,
				height: 80
			}
		},
		{
			title: 'Eye and dental care',
			description: 'Your staff are covered for eye and dental <br /> care consultations',
			icon: {
				icon: 'eye-dental',
				dir: 'fill',
				height: 60,
				width: 60
			}
		},
		{
			title: 'Specialist consultations',
			description:
				'Get access to specialist doctors for expert consultations tailored to your team’s unique healthcare needs.',
			icon: {
				icon: 'special-consultation',
				dir: 'fill',
				height: 80,
				width: 80
			}
		},
		{
			title: 'Virtual consultations',
			description: 'Your staff can connect virtually to medical doctors.',
			icon: {
				icon: 'virtual-consultation',
				dir: 'fill',
				height: 60,
				width: 60
			}
		}
	]
</script>

<template>
	<div>
		<header class="mt-0 bg-health-800 pt-12 sm:py-24">
			<lazy-ui-pad
				:ui="{ paddingBlock: '' }"
				class="w-full md:flex items-start justify-center gap-x-5 sm:justify-between flex-wrap gap-y-10"
			>
				<lazy-ui-h1
					paragraph="Designed for your business; Works for your staff. This is healthcare made affordable."
					:ui="btnClass"
					:primary-btn-text="`Enroll your staff in 5 minutes ${isMobile ? '' : 'or less'}`"
					secondary-btn-text="Speak to us to get on board"
					tag="FlexiCare"
					:tag-icon="{ dir: 'fill', icon: 'heart-rate-pulse' }"
					:action="{ type: 'onboard', product: 'flexicare' }"
					class="flex-1"
				>
					<template #title="{ highlightColor }">
						<span :class="highlightColor">Affordable and quality </span> healthcare for your
						<br class="xl:inline hidden" />
						staff
					</template>
				</lazy-ui-h1>

				<div class="px-3 md:px-0 flex items-center justify-center">
					<lazy-ui-image-frame
						alt="Smiling business owner representing Flexicare HMO"
						:src="{
							lg: '/v1727082905/black-business-owner-lg_plehjx.webp',
							sm: '/v1727082905/black-business-owner-sm_vzcgdm.webp'
						}"
						:ui="{
							pictureWrapper: {
								translate: '-translate-x-5 md:translate-y-5'
							},
							wrapper: {
								height: 'min-h-[330px] xl:min-h-max'
							}
						}"
					/>
				</div>
			</lazy-ui-pad>
		</header>

		<ui-pad :ui="{ paddingBlock: '', margin: 'sm:my-44 my-24' }" class="mx-auto">
			<lazy-molecules-partners />
			<ui-spacer />
			<lazy-main-health-care-experience
				title="Quality & affordable healthcare at <br /> your fingertips!"
				description="Our goal is to provide your team with the best possible tools and support, ensuring their health and well-being are our top priorities."
			/>
			<ui-spacer />

			<lazy-main-productivity-benefits
				product-name="FlexiCare"
				title="Benefits you and your team can bank on always!"
				description="Your employees get to enjoy these and much more!"
				:team-benefits="teamBenefits"
				@trigger="() => [(showBenefits = true), (focusMode = true)]"
			/>
		</ui-pad>
		<lazy-main-health-pricing
			product-name="flexicare"
			:product-code="flexiCareDetail?.code_name ?? ProductCode.FLEXICARE_HYGEIA_BUSINESS"
			title="Flexible products curated to match your <br class='max-md:hidden' /> business needs starting from ₦2,000"
			description="Compare benefits and choose the plan that feels right"
			@more-benefit="showBenefits = true"
		/>
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<lazy-main-most-used-hospitals :list="mostlyUsedHospitals" />
			<div class="flex justify-center pt-8 md:pt-0 animate-scale">
				<button
					type="button"
					class="default-btn p-[32px_24px] max-md:text-base md:hidden bg-health-500 custom-transition hover:bg-health-800 gap-x-3 font-semibold text-white"
					@click="showBenefits = true"
				>
					<tools-icon dir="random" icon="hospital-house" />
					View our complete list of hospitals
				</button>
			</div>
			<ui-spacer :ui="{ paddingBlock: '', marginBlock: ' sm:mb-24' }" />
			<lazy-molecules-hospital-list
				class="hidden md:block"
				group-key="flexicare"
				:product-code="flexiCareDetail?.code_name ?? ProductCode.FLEXICARE_HYGEIA_BUSINESS"
			/>
			<ui-spacer />
			<lazy-main-benefit-list-display
				product-category="health"
				product-name="flexicare"
				:product-code="flexiCareDetail?.code_name ?? ProductCode.FLEXICARE_HYGEIA_BUSINESS"
				:underwriter="flexiCareDetail?.underwriter ?? HealthProviders.HYGEIA"
				title="Health is wealth, and here, <br /> it's affordable."
				description="Find the right plan to fit your company's needs"
			/>
			<ui-spacer />

			<lazy-molecules-process-steps
				:processes="onboardingProcesses"
				title="Simple. Fast. Affordable."
				description="Onboard your team in 3 easy steps!"
				:ui="{ strokeText: { strokeColor: '#A6D7F2', color: 'text-health-100' }, header: { width: '' } }"
				@trigger="() => (isDialogOpen = true)"
			>
				<template #btn>
					<ui-btn
						:ui="{
							btn: {
								base: 'bg-health-500 mt-5 md:mt-8 min-h-[56px]',
								onHoverPseudo: 'hover:before:bg-health-800',
								padding: 'p-[18px_20px] md:p-[18px_80px]'
							},
							text: {
								base: 'text-white'
							}
						}"
						@click="navigateToProduct('flexicare')"
					>
						<span>Get covered now</span>
						<template #icon>
							<tools-icon dir="fill" icon="heart-rate-pulse" />
						</template>
					</ui-btn>
				</template>
			</lazy-molecules-process-steps>
		</ui-pad>
		<ui-spacer />

		<lazy-molecules-important-guides :guide-list="guideList" />
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-testimonials />
		</ui-pad>
		<ui-spacer />

		<molecules-demo-card
			title="Comprehensive coverage for your business."
			type="health"
			description=""
			@secondary-click="viewMoreProducts('health')"
		/>
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-investors class="pt-12 pb-20" />
			<main-faq :faq-list="faqList" />
		</ui-pad>
		<ui-spacer />

		<molecules-benefit-dialog
			v-model:show="showBenefits"
			v-model:focus-on="focusMode"
			:product-name="flexiCareDetail?.code_name ?? ProductCode.FLEXICARE_HYGEIA_BUSINESS"
			product-category="health"
		/>

		<lazy-molecules-auth-dialog v-model="isDialogOpen" selected="register" />
	</div>
</template>

<style scoped>
	@media screen and (max-width: 767px) {
		.animate-scale {
			animation: scale 1.3s linear infinite;
		}
	}

	@keyframes scale {
		0%,
		100% {
			transform: scale(0.95);
		}
		50% {
			transform: scale(1.05);
		}
	}
</style>
